/**
 * Padrões de feedback (U1+U5): toasts de sucesso/erro (Mantine notifications),
 * alerts de erro da API (com listas de diagnóstico completas) e modal de
 * confirmação para ações irreversíveis.
 */
import { Alert, List, Modal, Button, Text, Group } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ErroApi, listasDoErro } from "../api";

/** Toast de sucesso (mensagem objetiva). */
export function notificarOk(mensagem: string, titulo = "Pronto") {
  notifications.show({ title: titulo, message: mensagem, color: "sillis", position: "bottom-right" });
}

/** Toast AMARELO de aviso orientativo (a operação passou, mas há algo a
 *  revisar — ex.: saldo de férias negativo após o gozo, solicitação sem
 *  vínculo de período). */
export function notificarAviso(mensagem: string, titulo = "Registrado — atenção") {
  notifications.show({ title: titulo, message: mensagem, color: "yellow", position: "bottom-right" });
}

/** Toast de erro da API (título amigável + primeira linha do diagnóstico). */
export function notificarErroApi(e: unknown, tituloPadrao = "Não deu para concluir") {
  let titulo = tituloPadrao;
  let mensagem = "Erro inesperado.";
  if (e instanceof ErroApi) {
    titulo =
      e.status === 409
        ? "Não deu para concluir agora"
        : e.status === 422
          ? "Revise os dados"
          : e.status === 404
            ? "Não encontrado"
            : `Erro ${e.status}`;
    const listas = listasDoErro(e.corpo);
    mensagem = listas[0]?.itens[0] ?? "Erro na API — veja o detalhe na página.";
    if (listas.reduce((acc, l) => acc + l.itens.length, 0) > 1) mensagem += " (e mais — detalhes na página)";
  } else if (e instanceof Error) {
    mensagem = e.message;
  }
  notifications.show({ title: titulo, message: mensagem, color: "red", position: "bottom-right" });
}

/** Alert de erro da API com o diagnóstico COMPLETO (título amigável + lista). */
export function ErroApiAlert({ erro }: { erro: unknown }) {
  if (!erro) return null;
  if (erro instanceof ErroApi) {
    const listas = listasDoErro(erro.corpo);
    const titulo =
      erro.status === 409
        ? "Não deu para concluir agora"
        : erro.status === 422
          ? "Revise os dados"
          : erro.status === 404
            ? "Não encontrado"
            : `Erro ${erro.status}`;
    return (
      <Alert color="red" title={titulo} mb="sm" role="alert">
        {listas.length === 0 && <Text size="sm">{JSON.stringify(erro.corpo)}</Text>}
        {listas.map((l, i) => (
          <div key={i}>
            {l.titulo && (
              <Text size="sm" fw={700}>
                {l.titulo}:
              </Text>
            )}
            <List size="sm" withPadding>
              {l.itens.map((item, j) => (
                <List.Item key={j}>{item}</List.Item>
              ))}
            </List>
          </div>
        ))}
      </Alert>
    );
  }
  return (
    <Alert color="red" title="Erro" mb="sm" role="alert">
      {erro instanceof Error ? erro.message : String(erro)}
    </Alert>
  );
}

/** Alert de sucesso inline (as ações também avisam via toast). */
export function OkAlert({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return (
    <Alert color="sillis" mb="sm">
      {children}
    </Alert>
  );
}

/** Extrai os `avisos` de uma resposta da API (P2 — carga histórica: cadastro
 *  retroativo a mês fechado é PERMITIDO com aviso, nunca bloqueio). */
export function avisosDe(resposta: unknown): string[] {
  if (!resposta || typeof resposta !== "object") return [];
  const a = (resposta as { avisos?: unknown }).avisos;
  return Array.isArray(a) ? a.map(String) : [];
}

/** Alert AMARELO de avisos da operação (não é erro — a operação passou; ex.:
 *  cadastro histórico cobrindo meses fechados, que não mudam). */
export function AvisosAlert({ avisos }: { avisos: string[] }) {
  if (avisos.length === 0) return null;
  return (
    <Alert color="yellow" title="Registrado — atenção aos meses fechados" mb="sm" role="status">
      <List size="sm" withPadding>
        {avisos.map((a, i) => (
          <List.Item key={i}>{a}</List.Item>
        ))}
      </List>
    </Alert>
  );
}

/** Modal de confirmação (ações irreversíveis) — substitui o ConfirmDialog próprio. */
export function Confirmacao({
  aberta,
  titulo,
  consequencia,
  rotuloConfirmar = "Confirmar",
  onConfirmar,
  onCancelar,
}: {
  aberta: boolean;
  titulo: string;
  /** O que acontece se confirmar (linguagem simples, sem jargão). */
  consequencia: string;
  rotuloConfirmar?: string;
  onConfirmar: () => void;
  onCancelar: () => void;
}) {
  return (
    <Modal opened={aberta} onClose={onCancelar} title={titulo} centered>
      <Text size="sm" mb="md">
        {consequencia}
      </Text>
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancelar}>
          Voltar
        </Button>
        <Button color="sillis" onClick={onConfirmar}>
          {rotuloConfirmar}
        </Button>
      </Group>
    </Modal>
  );
}
